import { PostPage } from "@/components/formats/post-page";
import {
  MemeGrid,
  TitleShadow,
  CategoryGrid,
  CategoryContainer,
  MobileBackBtn,
} from "@/components/styles";
import useUserStore from "@/state/user/useUserStore";
import { Asset } from "@/types/types";
import React from "react";
import {
  ArrowBigLeft,
  Disc3,
  Eye,
  GraduationCap,
  Shirt,
  Smile,
} from "lucide-react";
import { BannerPage } from "@/components/formats/banner-page";
import { PostPageMobile } from "@/components/formats/post-page-mobile";
import Image from "@/components/ui/image";
import Link from "next/link";
import { m } from "motion/react";
import UploadMeme from "@/components/ui/upload-meme";

const MobileTemplatePage = () => {
  const [assetsCount, imageFormat] = useUserStore((state) => [
    state.assetsCount,
    state.imageFormat,
  ]);

  const HeadAssets = Array.from(
    { length: assetsCount?.base },
    (_, i) => `/Mememaker/base/base${i + 1}.png`
  );

  const ClothingAssets = Array.from(
    { length: assetsCount?.clothing },
    (_, i) => `/Mememaker/clothing/clothing${i + 1}.png`
  );

  const FaceAssets = Array.from(
    { length: assetsCount?.headgear },
    (_, i) => `/Mememaker/headgear/headgear${i + 1}.png`
  );

  const EyeAssets = Array.from(
    { length: assetsCount?.face },
    (_, i) => `/Mememaker/face/face${i + 1}.png`
  );

  const MiscAssets = Array.from(
    { length: assetsCount?.misc },
    (_, i) => `/Mememaker/misc/misc${i + 1}.png`
  ).reverse();

  const allAssets: Asset[] = [
    { assets: HeadAssets, icon: <Smile /> },
    { assets: ClothingAssets, icon: <Shirt /> },
    { assets: FaceAssets, icon: <GraduationCap /> },
    { assets: EyeAssets, icon: <Eye /> },
    { assets: MiscAssets, icon: <Disc3 /> },
  ];

  const templateCategories = [
    {
      title: "henlo",
      type: "henlo",
      path: "/henlo",
      background: "/Mememaker/henlo3/background/bg2.png",
    },
    {
      title: "the honey jar",
      type: "thj",
      path: "/thj",
      background: "/Mememaker/folders/thj.jpeg",
    },
  ];

  return (
    <div
      style={{
        height: "100vh",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        gap: "12px",
      }}
    >
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "space-between",
          position: "relative",
        }}
      >
        {(() => {
          switch (imageFormat) {
            case "banner":
              return (
                <BannerPage
                  color={"#2e64fb"}
                  assets={allAssets}
                  websiteLink="https://www.0xhoneyjar.xyz/"
                />
              );
            case "post":
              return (
                <PostPageMobile
                  color={"#2e64fb"}
                  assets={allAssets}
                  websiteLink="https://www.0xhoneyjar.xyz/"
                />
              );
            default:
              return (
                <div
                  style={{
                    width: "100%",
                    height: "auto",
                    display: "flex",
                    flexDirection: "column",
                    boxSizing: "border-box",
                    padding: "16px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      marginBottom: "16px",
                    }}
                  >
                    <div
                      style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "start",
                        justifyContent: "center",
                        flexDirection: "column",
                      }}
                    >
                      <TitleShadow style={{ margin: 0, marginBottom: "4px" }}>
                        Templates
                      </TitleShadow>
                      <p
                        style={{
                          margin: 0,
                          fontFamily: "var(--font-cabin-condensed)",
                        }}
                      >
                        Select a meme template from one of the following
                        projects or upload your own to start creating your own
                        memes.
                      </p>
                    </div>
                  </div>
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      flexDirection: "row",
                      gap: "16px",
                    }}
                  >
                    <CategoryGrid
                      style={{
                        gridTemplateColumns: "repeat(1, 1fr)",
                      }}
                    >
                      {templateCategories.map((category) => (
                        <Link
                          href={category.path ?? ""}
                          key={category.type}
                          style={{ textDecoration: "none" }}
                        >
                          <CategoryContainer>
                            {category.background && (
                              <m.div
                                whileHover={{
                                  scale: 1.05,
                                  transition: {
                                    duration: 0.15,
                                    ease: "easeOut",
                                  },
                                }}
                                style={{
                                  position: "absolute",
                                  width: "100%",
                                  height: "100%",
                                  left: 0,
                                  top: 0,
                                }}
                              >
                                <Image
                                  src={category.background}
                                  alt={"background"}
                                  fill
                                  style={{
                                    objectFit: "cover",
                                  }}
                                />
                                <div
                                  style={{
                                    position: "absolute",
                                    bottom: 0,
                                    left: 0,
                                    width: "100%",
                                    height: "25%",
                                    background:
                                      "linear-gradient(to bottom, transparent, rgba(0,0,0,0.75))",
                                  }}
                                />
                              </m.div>
                            )}
                            <div
                              style={{
                                fontFamily: "var(--font-cabin-condensed)",
                                fontSize: "1.2rem",
                                color: "#000",
                                zIndex: 100,
                                position: "absolute",
                                bottom: 16,
                                left: 16,
                                textAlign: "center",
                              }}
                            >
                              <TitleShadow
                                style={{
                                  padding: 0,
                                  margin: 0,
                                  fontSize: "1.6rem",
                                }}
                              >
                                {category.title}
                              </TitleShadow>
                            </div>
                          </CategoryContainer>
                        </Link>
                      ))}
                    </CategoryGrid>
                  </div>
                </div>
              );
          }
        })()}
      </div>
    </div>
  );
};

export default MobileTemplatePage;
